/**
 * 圖示來源：Lucide React（通用圖示）與 Simple Icons（品牌 Logo）。
 * 供 IconPanel 選單與 pipeline 輸出繪製使用。
 */
import {
  Heart,
  Star,
  Check,
  Circle,
  X,
  Camera,
  Type,
  Image as ImageIcon,
} from 'lucide-react';
import {
  SiReact,
  SiGithub,
  SiX,
  SiFacebook,
  SiInstagram,
  SiYoutube,
  SiApple,
  SiGoogle,
  SiDiscord,
  SiSpotify,
  SiLine,
  SiWhatsapp,
} from '@icons-pack/react-simple-icons';

export type IconSource = 'lucide' | 'simple';

export const LUCIDE_ICONS: { id: string; label: string }[] = [
  { id: 'Heart', label: '愛心' },
  { id: 'Star', label: '星星' },
  { id: 'Check', label: '勾選' },
  { id: 'Circle', label: '圓形' },
  { id: 'X', label: '叉' },
  { id: 'Camera', label: '相機' },
  { id: 'Type', label: '文字' },
  { id: 'ImageIcon', label: '圖片' },
];

export const SIMPLE_ICONS: { slug: string; label: string }[] = [
  { slug: 'react', label: 'React' },
  { slug: 'github', label: 'GitHub' },
  { slug: 'x', label: 'X' },
  { slug: 'facebook', label: 'Facebook' },
  { slug: 'instagram', label: 'Instagram' },
  { slug: 'youtube', label: 'YouTube' },
  { slug: 'apple', label: 'Apple' },
  { slug: 'google', label: 'Google' },
  { slug: 'discord', label: 'Discord' },
  { slug: 'spotify', label: 'Spotify' },
  { slug: 'line', label: 'LINE' },
  { slug: 'whatsapp', label: 'WhatsApp' },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Lucide/Simple Icons 元件 props 相容即可
const LUCIDE_MAP: Record<string, React.ComponentType<any>> = {
  Heart,
  Star,
  Check,
  Circle,
  X,
  Camera,
  Type,
  ImageIcon,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- 同上
const SIMPLE_MAP: Record<string, React.ComponentType<any>> = {
  react: SiReact,
  github: SiGithub,
  x: SiX,
  facebook: SiFacebook,
  instagram: SiInstagram,
  youtube: SiYoutube,
  apple: SiApple,
  google: SiGoogle,
  discord: SiDiscord,
  spotify: SiSpotify,
  line: SiLine,
  whatsapp: SiWhatsapp,
};

/**
 * 將圖示轉成可繪製到 canvas 的圖片（data URL 或 Image）。
 * Lucide 以 React 渲染成 SVG 再轉圖；Simple Icons 使用 simple-icons 的 SVG 字串。
 */
export async function getIconAsImage(
  source: IconSource,
  iconId: string,
  color: string,
  sizePx: number
): Promise<HTMLImageElement> {
  const size = Math.max(16, Math.min(256, sizePx));
  let svgString: string;

  if (source === 'lucide') {
    let IconComponent = LUCIDE_MAP[iconId];
    if (!IconComponent) {
      const lucideModule = await import('lucide-react');
      IconComponent = (
        lucideModule as unknown as Record<string, React.ComponentType>
      )[iconId];
    }
    if (!IconComponent) throw new Error(`Unknown Lucide icon: ${iconId}`);
    svgString = await renderLucideToSvg(IconComponent, color, size);
  } else {
    svgString = await getSimpleIconSvg(iconId, color, size);
  }

  const dataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgString)))}`;
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Icon image load failed'));
    img.src = dataUrl;
  });
}

function rgbToHex(rgb: string): string | null {
  const m = rgb.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!m) return null;
  const r = Number(m[1]).toString(16).padStart(2, '0');
  const g = Number(m[2]).toString(16).padStart(2, '0');
  const b = Number(m[3]).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

/** 將任意 hex 正規化為 6 位小寫 #rrggbb，便於合併 #333 與 #333333 計數 */
function normalizeHex(raw: string): string | null {
  const lower = raw.trim().toLowerCase();
  const hex3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(lower);
  if (hex3)
    return `#${hex3[1]}${hex3[1]}${hex3[2]}${hex3[2]}${hex3[3]}${hex3[3]}`;
  const hex6 = /^#([0-9a-f]{6})$/.exec(lower);
  if (hex6) return `#${hex6[1]}`;
  return null;
}

/** 從 SVG 字串中找出出現次數最多的 fill/stroke 顏色（排除透明、白、none），供主色替換用。支援屬性 fill="..." 與 style="fill: ..."。 */
function getDominantColor(svgString: string): string | null {
  const count: Record<string, number> = {};
  const add = (raw: string) => {
    if (
      raw === 'none' ||
      raw === 'transparent' ||
      raw === '#fff' ||
      raw === '#ffffff' ||
      raw === 'white' ||
      raw === 'currentcolor'
    )
      return;
    let key: string = raw.trim().toLowerCase();
    const hex = normalizeHex(key);
    if (hex) key = hex;
    else if (key.startsWith('rgb')) {
      const h = rgbToHex(key);
      if (h) key = h;
    }
    count[key] = (count[key] ?? 0) + 1;
  };
  for (const m of svgString.matchAll(
    /\b(?:fill|stroke)\s*=\s*["']([^"']+)["']/gi
  ))
    add(m[1]);
  for (const m of svgString.matchAll(/\b(?:fill|stroke)\s*:\s*([^;}"']+)/gi))
    add(m[1]);
  let maxCount = 0;
  let dominant: string | null = null;
  for (const [color, n] of Object.entries(count)) {
    if (n > maxCount) {
      maxCount = n;
      dominant = color;
    }
  }
  return dominant;
}

/** 回傳要替換的顏色字串列表：正規化後的 6 位 hex 與其 3 位簡寫（若相同），以便 SVG 中任一種寫法都被替換 */
function dominantReplaceVariants(dominantHex: string): string[] {
  const list = [dominantHex];
  const m = /^#([0-9a-f])\1([0-9a-f])\2([0-9a-f])\3$/.exec(
    dominantHex.toLowerCase()
  );
  if (m) list.push(`#${m[1]}${m[2]}${m[3]}`);
  return list;
}

/** 將 SVG 中「大形狀」且為 currentColor 或 dominant 的 fill 改為 none，避免主色替換後出現整塊色塊 */
function neutralizeLargeShapesInSvg(
  svgString: string,
  dominant: string | null
): string {
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const hasTargetFill = (tag: string): boolean => {
    if (/fill\s*=\s*["']currentColor["']/i.test(tag)) return true;
    if (dominant) {
      const variants = dominantReplaceVariants(dominant);
      for (const v of variants) {
        const re = new RegExp(`\\bfill\\s*=\\s*["']${esc(v)}["']`, 'i');
        if (re.test(tag)) return true;
      }
    }
    return false;
  };
  const setFillNone = (tag: string): string => {
    let t = tag.replace(/\bfill\s*=\s*["']currentColor["']/gi, 'fill="none"');
    if (dominant) {
      for (const v of dominantReplaceVariants(dominant)) {
        const re = new RegExp(`(\\bfill\\s*=\\s*["'])${esc(v)}(["'])`, 'gi');
        t = t.replace(re, '$1none$2');
      }
    }
    return t.replace(/\bfill\s*:\s*currentColor\s*([;}"'])/gi, 'fill: none$1');
  };
  let out = svgString;
  out = out.replace(/<rect\s[^>]*>/gi, tag => {
    if (!hasTargetFill(tag)) return tag;
    const hasFullSize =
      (/width\s*=\s*["']100%["']/i.test(tag) &&
        /height\s*=\s*["']100%["']/i.test(tag)) ||
      (/width\s*=\s*["']\d{3,}/i.test(tag) &&
        /height\s*=\s*["']\d{3,}/i.test(tag));
    return hasFullSize ? setFillNone(tag) : tag;
  });
  out = out.replace(/<path\s[^>]*>/gi, tag => {
    if (!hasTargetFill(tag)) return tag;
    const dMatch = tag.match(/\bd\s*=\s*["']([^"']*)["']/i);
    const d = dMatch ? dMatch[1] : '';
    const hasLargeArc =
      /\d{2,}\.\d+,\s*\d{2,}\.\d+,\s*0\s*,\s*1\s*,\s*1\s*[,-]/.test(d) ||
      /a\s*\d{2,}\.\d+\s*,?\s*\d{2,}\.\d+\s*,?\s*0\s*,?\s*1\s*,?\s*1\s*[,-]?/i.test(
        d
      ) ||
      (/\d{3,}/.test(d) && /0\s*,?\s*1\s*,?\s*1\s*[,-]/.test(d));
    const nums = (d.match(/\d{2,}\.?\d*/g) || []).map(Number);
    const bigCoords =
      nums.some(n => n >= 400) || nums.filter(n => n >= 200).length >= 3;
    const hasLargeShape = hasLargeArc || (d.length > 100 && bigCoords);
    return hasLargeShape ? setFillNone(tag) : tag;
  });
  out = out.replace(/<circle\s[^>]*>/gi, tag => {
    if (!hasTargetFill(tag)) return tag;
    const rMatch = tag.match(/\br\s*=\s*["']([^"']*)["']/i);
    const r = rMatch ? Number(rMatch[1]) : 0;
    return r >= 80 ? setFillNone(tag) : tag;
  });
  out = out.replace(/<ellipse\s[^>]*>/gi, tag => {
    if (!hasTargetFill(tag)) return tag;
    const rxMatch = tag.match(/\brx\s*=\s*["']([^"']*)["']/i);
    const ryMatch = tag.match(/\bry\s*=\s*["']([^"']*)["']/i);
    const rx = rxMatch ? Number(rxMatch[1]) : 0;
    const ry = ryMatch ? Number(ryMatch[1]) : 0;
    return rx >= 80 || ry >= 80 ? setFillNone(tag) : tag;
  });
  return out;
}

const DOODLE_DATAURL_CACHE = new Map<string, string>();

/** 需經後端 proxy 才能繞過 CORS 的網域（與後端 proxy-svg 白名單一致） */
const PROXY_ALLOWED_HOSTS = new Set([
  'opendoodles.s3-us-west-1.amazonaws.com',
  'opendoodles.s3.amazonaws.com',
]);

function shouldUseProxy(svgUrl: string): boolean {
  try {
    const u = new URL(svgUrl);
    return u.protocol === 'https:' && PROXY_ALLOWED_HOSTS.has(u.hostname);
  } catch {
    return false;
  }
}

function getApiBase(): string {
  return (
    (import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env
      ?.VITE_API_BASE ?? ''
  );
}

/**
 * 從塗鴉 SVG URL 載入並轉成可繪製到 canvas 的圖片。
 * 僅縮放（sizePx）。可選 primaryColor：只替換 SVG 中最常出現的那個顏色，其餘保留，維持多色。
 * 結果依 (url, size, color) 快取，供預覽與匯出重複使用。
 */
export async function getDoodleAsImage(
  svgUrl: string,
  sizePx: number,
  primaryColor?: string
): Promise<HTMLImageElement> {
  const size = Math.max(16, Math.min(512, sizePx));
  const colorKey = primaryColor?.trim() ?? '';
  const cacheKey = `${svgUrl}|${size}|${colorKey}`;
  const cached = DOODLE_DATAURL_CACHE.get(cacheKey);
  if (cached) {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Doodle image load failed'));
      img.src = cached;
    });
  }
  const apiBase = getApiBase();
  const fetchUrl =
    apiBase && shouldUseProxy(svgUrl)
      ? `${apiBase.replace(/\/$/, '')}/api/proxy-svg?url=${encodeURIComponent(svgUrl)}`
      : svgUrl;
  const res = await fetch(fetchUrl, { mode: 'cors' });
  if (!res.ok) throw new Error(`Doodle fetch failed: ${res.status}`);
  let svgString = await res.text();
  const widthAttr = `width="${size}"`;
  const heightAttr = `height="${size}"`;
  if (!/width\s*=\s*["']/.test(svgString))
    svgString = svgString.replace(/<svg/, `<svg ${widthAttr} ${heightAttr}`);
  else
    svgString = svgString
      .replace(/\bwidth\s*=\s*["'][^"']*["']/g, widthAttr)
      .replace(/\bheight\s*=\s*["'][^"']*["']/g, heightAttr);
  if (colorKey) {
    const c = primaryColor!
      .trim()
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;');
    const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const dominant = getDominantColor(svgString);
    // 先將「大形狀」改為不填色，再替換主色，避免整塊色塊
    svgString = neutralizeLargeShapesInSvg(svgString, dominant);
    // 替換 currentColor 與 dominant 為使用者主色
    svgString = svgString.replace(
      /\bfill\s*=\s*["']currentColor["']/gi,
      `fill="${c}"`
    );
    svgString = svgString.replace(
      /\bstroke\s*=\s*["']currentColor["']/gi,
      `stroke="${c}"`
    );
    svgString = svgString.replace(
      /\b(fill|stroke)\s*:\s*currentColor\s*([;}"'])/gi,
      (_: string, prop: string, suffix: string) => `${prop}: ${c}${suffix}`
    );
    if (dominant) {
      const variants = dominantReplaceVariants(dominant);
      for (const variant of variants) {
        const reAttr = new RegExp(
          `(\\b(?:fill|stroke)\\s*=\\s*["'])${esc(variant)}(["'])`,
          'gi'
        );
        svgString = svgString.replace(reAttr, `$1${c}$2`);
        const reStyle = new RegExp(
          `(\\b(?:fill|stroke)\\s*:\\s*)${esc(variant)}([;}"'])`,
          'gi'
        );
        svgString = svgString.replace(reStyle, `$1${c}$2`);
      }
    }
  }
  const dataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgString)))}`;
  DOODLE_DATAURL_CACHE.set(cacheKey, dataUrl);
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Doodle image load failed'));
    img.src = dataUrl;
  });
}

async function renderLucideToSvg(
  Icon: React.ComponentType<{ size?: number; color?: string }>,
  color: string,
  size: number
): Promise<string> {
  const { createRoot } = await import('react-dom/client');
  const React = await import('react');
  const div = document.createElement('div');
  document.body.appendChild(div);
  const root = createRoot(div);
  root.render(React.createElement(Icon, { size, color }));
  await new Promise(r => setTimeout(r, 0));
  const svg = div.querySelector('svg');
  const str = svg ? svg.outerHTML : '';
  root.unmount();
  document.body.removeChild(div);
  return str;
}

async function getSimpleIconSvg(
  slug: string,
  color: string,
  size: number
): Promise<string> {
  const si = await import('simple-icons');
  const mod = si as unknown as Record<string, { slug?: string; svg?: string }>;
  let icon: { svg?: string } | undefined;
  for (const key of Object.keys(mod)) {
    if (key === 'default') continue;
    const v = mod[key];
    if (v && typeof v === 'object' && v.slug === slug && v.svg) {
      icon = v;
      break;
    }
  }
  if (!icon?.svg) throw new Error(`Unknown Simple Icon: ${slug}`);
  const escapedColor = color.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  const svg = icon.svg
    .replace(/<svg/, `<svg width="${size}" height="${size}"`)
    .replace(/\bfill="[^"]*"/g, `fill="${escapedColor}"`);
  return svg;
}

export function getLucideComponent(
  id: string
): React.ComponentType<{ size?: number; color?: string }> | undefined {
  return LUCIDE_MAP[id] as
    | React.ComponentType<{ size?: number; color?: string }>
    | undefined;
}

export function getSimpleIconComponent(
  slug: string
): React.ComponentType<{ size?: number; color?: string }> | undefined {
  return SIMPLE_MAP[slug] as
    | React.ComponentType<{ size?: number; color?: string }>
    | undefined;
}

/** 供搜尋用：取得完整 Lucide 圖示名稱列表（介面未列出的全量）。 */
export async function getLucideIconNamesForSearch(): Promise<string[]> {
  const mod = await import('lucide-react');
  const m = mod as unknown as Record<string, unknown>;
  return Object.keys(m).filter(
    k =>
      k !== 'default' &&
      k[0] === k[0].toUpperCase() &&
      typeof m[k] === 'function'
  );
}

/** 供搜尋用：取得完整 Simple Icons 列表（slug、title、siKey 供動態渲染）。 */
export async function getSimpleIconsForSearch(): Promise<
  { slug: string; title: string; siKey: string }[]
> {
  const si = await import('simple-icons');
  const mod = si as unknown as Record<
    string,
    { slug?: string; title?: string }
  >;
  const list: { slug: string; title: string; siKey: string }[] = [];
  for (const key of Object.keys(mod)) {
    if (key === 'default') continue;
    const v = mod[key];
    if (v && typeof v === 'object' && v.slug && v.title) {
      list.push({ slug: v.slug, title: v.title, siKey: key });
    }
  }
  return list;
}

/** 依名稱取得 Lucide 元件（先查表，再動態從 lucide-react 取）。 */
export async function getLucideComponentAsync(
  name: string
): Promise<React.ComponentType<{ size?: number; color?: string }> | undefined> {
  const fromMap = LUCIDE_MAP[name];
  if (fromMap)
    return fromMap as React.ComponentType<{ size?: number; color?: string }>;
  const mod = await import('lucide-react');
  return (mod as unknown as Record<string, React.ComponentType>)[name] as
    | React.ComponentType<{ size?: number; color?: string }>
    | undefined;
}

/** 依 slug 取得 Simple Icon 元件（先查表，再動態從 @icons-pack 取）。 */
export async function getSimpleIconComponentAsync(
  slug: string
): Promise<React.ComponentType<{ size?: number; color?: string }> | undefined> {
  const fromMap = SIMPLE_MAP[slug];
  if (fromMap)
    return fromMap as React.ComponentType<{ size?: number; color?: string }>;
  const si = await import('simple-icons');
  const simod = si as unknown as Record<string, { slug?: string }>;
  let siKey: string | null = null;
  for (const key of Object.keys(simod)) {
    if (key === 'default') continue;
    const v = simod[key];
    if (v && typeof v === 'object' && v.slug === slug) {
      siKey = key; // e.g. siReact
      break;
    }
  }
  if (!siKey) return undefined;
  const reactKey = 'S' + siKey.slice(1); // SiReact
  const pack = await import('@icons-pack/react-simple-icons');
  return (pack as unknown as Record<string, React.ComponentType>)[reactKey] as
    | React.ComponentType<{ size?: number; color?: string }>
    | undefined;
}
